import { useEffect, useId, useLayoutEffect, useRef, useState, type CSSProperties, type KeyboardEvent, type ReactNode } from "react";
import { Glyph as GIcon } from "./icons";
import "./select.css";

export type SelectOption = { value: string; label: string; description?: string; disabled?: boolean; group?: string; searchTerms?: string;
  /** Optional hue (0–360) that tints the option and its group title, e.g. a model family colour. */ tone?: number };
type SelectRenderState = { selected: boolean; active: boolean };

/** Shared Celia listbox. Values remain feature-owned; selecting never writes to an API. */
export function Select({ value, options, onChange, label, id, name, placeholder = "Choose…", searchable = false,
  searchPlaceholder = "Search options…", disabled = false, disabledReason, loading = false, error, onRetry,
  emptyText = "No options available", icon, footer, size = "compact", className = "", testId, describedBy, invalid, required,
  renderOption, renderPreview, searchValue, onSearchChange, selectedOption, menuHeader, menuFooter, inspectDisabledOptions = false, layout = "list" }: {
  value: string; options: readonly SelectOption[]; onChange: (value: string) => void; label: string;
  id?: string; name?: string; placeholder?: string; searchable?: boolean; searchPlaceholder?: string;
  disabled?: boolean; disabledReason?: string; loading?: boolean; error?: string; onRetry?: () => void;
  emptyText?: string; icon?: ReactNode; footer?: { label: string; onClick: () => void };
  size?: "compact" | "form"; className?: string; testId?: string; describedBy?: string; invalid?: boolean | "true" | "false" | "grammar" | "spelling"; required?: boolean;
  renderOption?: (option: SelectOption, state: SelectRenderState) => ReactNode;
  /** Optional read-only preview for the keyboard-active or clicked choice; pointer hover never builds it. */
  renderPreview?: (option: SelectOption | undefined) => ReactNode;
  /** Server-owned catalogs supply an already filtered page and preserve the selected item across pages. */
  searchValue?: string; onSearchChange?: (value: string) => void; selectedOption?: SelectOption;
  menuHeader?: ReactNode;
  /** Read-only details actions stay outside listbox options; closing never chooses the highlighted value. */
  menuFooter?: ReactNode | ((highlighted: SelectOption | undefined, close: () => void) => ReactNode);
  inspectDisabledOptions?: boolean;
  /** `grid` lays options out as cards in a wider popup, so a long catalog is scanned rather than scrolled. */
  layout?: "list" | "grid";
}) {
  const uid = useId();
  const triggerId = id ?? `${uid}-trigger`;
  const listId = `${uid}-list`;
  const trigger = useRef<HTMLButtonElement>(null);
  const popup = useRef<HTMLDivElement>(null);
  const search = useRef<HTMLInputElement>(null);
  const footerButton = useRef<HTMLButtonElement>(null);
  const typeahead = useRef({ text: "", at: 0 });
  const [open, setOpen] = useState(false);
  const [localQuery, setLocalQuery] = useState("");
  const query = searchValue ?? localQuery;
  const setQuery = onSearchChange ?? setLocalQuery;
  const [active, setActive] = useState(value);
  const hasPreview = Boolean(renderPreview);
  const selected = options.find((option) => option.value === value) ?? selectedOption;
  const filtered = onSearchChange ? options : options.filter((option) => `${option.label} ${option.description ?? ""} ${option.group ?? ""} ${option.searchTerms ?? ""}`.toLocaleLowerCase().includes(query.trim().toLocaleLowerCase()));
  const available = loading || error ? [] : filtered.filter((option) => inspectDisabledOptions || !option.disabled);
  const groups = new Map<string, SelectOption[]>();
  for (const option of filtered) groups.set(option.group ?? "", [...(groups.get(option.group ?? "") ?? []), option]);
  const focused = available.find((option) => option.value === active) ?? available[0];
  const optionId = (option: SelectOption) => `${uid}-option-${options.indexOf(option)}`;

  function close(restore = false) {
    setOpen(false);
    if (restore) trigger.current?.focus();
  }
  function show() {
    if (disabled) return;
    if (!onSearchChange) setQuery("");
    setActive(value);
    setOpen(true);
  }
  function choose(next: string) {
    if (!options.some(option => option.value === next && !option.disabled)) return;
    close(true);
    onChange(next);
  }

  useLayoutEffect(() => {
    const menu = popup.current;
    const button = trigger.current;
    if (!menu || !button) return;
    if (!open) {
      if (menu.matches(":popover-open")) menu.hidePopover();
      return;
    }
    // Native top layer stays above parent modal dialogs without escaping their focus scope.
    if (typeof menu.showPopover === "function" && !menu.matches(":popover-open")) menu.showPopover();
    function position() {
      if (!menu || !button) return;
      const rect = button.getBoundingClientRect();
      const viewport = window.visualViewport;
      const leftEdge = (viewport?.offsetLeft ?? 0) + 12;
      const topEdge = (viewport?.offsetTop ?? 0) + 12;
      const rightEdge = leftEdge + (viewport?.width ?? innerWidth) - 24;
      const bottomEdge = topEdge + (viewport?.height ?? innerHeight) - 24;
      const width = Math.min(Math.max(Math.min(rect.width, 420), searchable ? 300 : 220, hasPreview ? 680 : 0, layout === "grid" ? 560 : 0), rightEdge - leftEdge);
      // Measure wrapping at the final width, not the width left over from a previous opening.
      menu.style.width = `${width}px`;
      const below = Math.max(0, bottomEdge - rect.bottom - 6);
      const above = Math.max(0, rect.top - topEdge - 6);
      const up = below < 200 && above > below;
      const height = Math.min(hasPreview ? 470 : layout === "grid" ? 460 : options.some(option => option.group) ? 400 : 320, up ? above : below);
      const body = menu.querySelector<HTMLElement>(".celia-select-body");
      const list = menu.querySelector<HTMLElement>(".celia-select-list");
      const choices = menu.querySelector<HTMLElement>(".celia-select-options");
      const preview = menu.querySelector<HTMLElement>(".celia-select-preview");
      const previewContent = menu.querySelector<HTMLElement>(".celia-select-preview-content");
      const childrenHeight = (element: HTMLElement | null, omit?: Element | null) =>
        Array.from(element?.children ?? []).filter(child => child !== omit).reduce((sum, child) => sum + child.getBoundingClientRect().height, 0);
      const choicesStyle = choices ? getComputedStyle(choices) : null;
      const choicesHeight = childrenHeight(choices) + (choicesStyle ? parseFloat(choicesStyle.paddingTop) + parseFloat(choicesStyle.paddingBottom) : 0);
      const previewStyle = preview ? getComputedStyle(preview) : null;
      const previewHeight = (previewContent?.getBoundingClientRect().height ?? 0) + (previewStyle ? parseFloat(previewStyle.paddingTop) + parseFloat(previewStyle.paddingBottom) : 0);
      const contentHeight = childrenHeight(menu, body) + Math.max(choicesHeight + childrenHeight(list, choices), previewHeight) + 2;
      // A definite height lets the nested flex list scroll instead of clipping its last options.
      const popupHeight = Math.min(height, contentHeight);
      menu.style.maxHeight = `${height}px`;
      menu.style.height = `${popupHeight}px`;
      menu.style.left = `${Math.max(leftEdge, Math.min(rect.left, rightEdge - width))}px`;
      menu.style.top = `${up ? Math.max(topEdge, rect.top - 6 - popupHeight) : rect.bottom + 6}px`;
      menu.dataset.placement = up ? "above" : "below";
    }
    position();
    const observer = new ResizeObserver(position);
    observer.observe(button);
    observer.observe(menu);
    const previewContent = menu.querySelector(".celia-select-preview-content");
    if (previewContent) observer.observe(previewContent);
    window.addEventListener("resize", position);
    window.addEventListener("scroll", position, true);
    window.visualViewport?.addEventListener("resize", position);
    window.visualViewport?.addEventListener("scroll", position);
    (searchable ? search.current : button)?.focus();
    return () => {
      observer.disconnect();
      window.removeEventListener("resize", position);
      window.removeEventListener("scroll", position, true);
      window.visualViewport?.removeEventListener("resize", position);
      window.visualViewport?.removeEventListener("scroll", position);
      if (menu.matches(":popover-open")) menu.hidePopover();
    };
  }, [open, searchable, hasPreview, layout, filtered.length, query, loading, error]);

  useEffect(() => { if (disabled) close(); }, [disabled]);
  useEffect(() => {
    if (!open) return;
    const outside = (event: PointerEvent) => {
      if (event.target instanceof Node && !popup.current?.contains(event.target) && !trigger.current?.contains(event.target)) close();
    };
    const blur = (event: FocusEvent) => {
      if (event.target instanceof Node && !popup.current?.contains(event.target) && !trigger.current?.contains(event.target)) close();
    };
    document.addEventListener("pointerdown", outside, true);
    document.addEventListener("focusin", blur);
    return () => { document.removeEventListener("pointerdown", outside, true); document.removeEventListener("focusin", blur); };
  }, [open]);
  useEffect(() => {
    if (open && focused) document.getElementById(optionId(focused))?.scrollIntoView({ block: "nearest" });
  }, [open, focused?.value, query]);

  function keydown(event: KeyboardEvent<HTMLElement>) {
    if (event.key === "Escape" && open) {
      event.preventDefault(); event.stopPropagation(); close(true); return;
    }
    if (event.key === "Tab" && open) {
      // Catalog filters and paging controls participate in normal focus order; outside focus closes the popup.
      if (menuHeader || menuFooter) return;
      // Search -> footer is a deliberate stop. Otherwise resume the page's tab order at the trigger.
      if (event.target === search.current && !event.shiftKey && footerButton.current) {
        event.preventDefault(); footerButton.current.focus(); return;
      }
      if (event.target === footerButton.current && event.shiftKey && search.current) {
        event.preventDefault(); search.current.focus(); return;
      }
      if (popup.current?.contains(event.target as Node)) {
        trigger.current?.focus();
      }
      close(); return;
    }
    if (event.target === footerButton.current || event.target instanceof HTMLButtonElement && popup.current?.contains(event.target)
      || (menuHeader || menuFooter) && event.target instanceof HTMLSelectElement) return;
    if (["ArrowDown", "ArrowUp", "Home", "End"].includes(event.key) && (open || event.key.startsWith("Arrow"))) {
      event.preventDefault();
      if (!open) { show(); return; }
      const index = available.findIndex((option) => option.value === focused?.value);
      const next = event.key === "Home" ? 0 : event.key === "End" ? available.length - 1
        : (index + (event.key === "ArrowDown" ? 1 : -1) + available.length) % available.length;
      if (available[next]) setActive(available[next].value);
    } else if (event.key === "Enter" || event.key === " " && !searchable) {
      if (!open) return; // Button's native click opens the menu.
      event.preventDefault(); if (focused) choose(focused.value);
    } else if (!searchable && event.key.length === 1 && !event.altKey && !event.ctrlKey && !event.metaKey) {
      const now = Date.now();
      const text = now - typeahead.current.at > 700 ? event.key : typeahead.current.text + event.key;
      typeahead.current = { text, at: now };
      const match = options.find((option) => !option.disabled && option.label.toLocaleLowerCase().startsWith(text.toLocaleLowerCase()));
      if (match) { event.preventDefault(); if (!open) show(); setActive(match.value); }
    }
  }

  return <div className={`celia-select ${className}`} data-size={size} data-placeholder={!selected || undefined}>
    {name && <input type="hidden" name={name} value={value} disabled={disabled} />}
    <button ref={trigger} id={triggerId} type="button" role={searchable ? undefined : "combobox"}
      className="celia-select-trigger" disabled={disabled} title={disabled ? disabledReason : selected?.label}
      aria-label={label} aria-describedby={describedBy} aria-invalid={invalid} aria-required={required} aria-haspopup="listbox" aria-expanded={open} aria-controls={open ? listId : undefined}
      aria-activedescendant={!searchable && open && focused ? optionId(focused) : undefined}
      data-testid={testId} onClick={() => open ? close() : show()} onKeyDown={keydown}>
      {icon}<span className="celia-select-value">{selected?.label ?? placeholder}</span>
      <GIcon name="expand_more" size={18} className="celia-select-chevron" />
    </button>
    <div ref={popup} popover="manual" className="celia-select-popup" data-open={open || undefined} onKeyDown={keydown}>
      {open && <>
      {searchable && <div className="celia-select-search">
        <GIcon name="search" size={16} />
        <input ref={search} role="combobox" aria-label={`Search ${label.toLocaleLowerCase()}`} aria-expanded={open}
          aria-controls={listId} aria-autocomplete="list" aria-activedescendant={focused ? optionId(focused) : undefined}
          value={query} placeholder={searchPlaceholder} autoComplete="off" onChange={(event) => { setQuery(event.target.value); setActive(""); }} />
        <button type="button" className="celia-select-close" aria-label={`Close ${label.toLocaleLowerCase()} choices`} onClick={() => close(true)}><GIcon name="close" size={16} /></button>
      </div>}
      {menuHeader}
      <div className="celia-select-body" data-preview={renderPreview ? "true" : undefined}>
        <div className="celia-select-list">
          <div id={listId} role="listbox" tabIndex={-1} aria-label={label} aria-busy={loading || undefined} className="celia-select-options" data-layout={layout === "grid" ? "grid" : undefined}>
            {!loading && !error && [...groups].map(([group, entries]) => <div key={group} role={group ? "group" : undefined} aria-label={group || undefined}>
              {group && <div className="celia-select-group-title" aria-hidden="true" style={entries[0]?.tone == null ? undefined : { "--tone": entries[0].tone } as CSSProperties}>{group}</div>}
              {entries.map((option) => <div key={option.value} id={optionId(option)} role="option"
              aria-selected={option.value === value} aria-disabled={option.disabled || undefined}
              className="celia-select-option" data-active={option.value === focused?.value || undefined} style={option.tone == null ? undefined : { "--tone": option.tone } as CSSProperties}
              title={option.description ? `${option.label} · ${option.description}` : option.label}
              onPointerDown={(event) => { event.preventDefault(); if (inspectDisabledOptions || !option.disabled) setActive(option.value); }}
              onClick={(event) => { event.preventDefault(); if (!option.disabled) choose(option.value); }}>
              {renderOption ? renderOption(option, { selected: option.value === value, active: option.value === focused?.value }) :
                <span className="celia-select-option-copy"><span>{option.label}</span>{option.description && <small>{option.description}</small>}</span>}
              {option.value === value && <GIcon name="check" size={18} />}
            </div>)}
            </div>)}
          </div>
          {(loading || error || !filtered.length) && <div className="celia-select-message" role="status">
            {loading ? "Loading options…" : error ?? (query ? "No matches. Try another name or slug." : emptyText)}
            {error && onRetry && <button type="button" onClick={onRetry}>Retry</button>}
          </div>}
        </div>
        {renderPreview && <aside className="celia-select-preview" aria-label={`${label} preview`} aria-live="polite">
          <div className="celia-select-preview-content">{renderPreview(focused)}</div>
        </aside>}
      </div>
      {typeof menuFooter === "function" ? menuFooter(focused, () => close(true)) : menuFooter}
      {footer && <div className="celia-select-footer"><button ref={footerButton} type="button" onClick={() => { close(true); footer.onClick(); }}>
        <span>{footer.label}</span><GIcon name="arrow_forward" size={16} />
      </button></div>}
      </>}
    </div>
  </div>;
}
